"use client"
import { useEffect } from "react"
import { HamburgerMenuIcon } from "@radix-ui/react-icons"
import finalLogo from "../assets/images/final-logo.svg"
// import bellIcon from "../assets/icons/bell.svg"
// import questionIcon from "../assets/icons/question.svg"
import { useState } from "react"
import { SidebarItem } from "./shared/SidebarItem";
import { mainNavItems, otherNavItems } from "../config/sidebarItems";
import Button from "../components/shared/Button";
import { Plus } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthContext } from "../components/AuthProvider";
import CreateDocumentModal from '../components/modals/createDocumentModal'; 
import ConfirmationModal from "../components/modals/confirmationModal";
import logoutModalIcon from "../assets/icons/logout-modal-icon.svg";
import EditProfileModal from "./modals/editProfileModal";
import ChangePasswordModal from "./modals/changePasswordModal";
import Modal from "./modals/modal";
import profileUpdatedModalIcon from "../assets/icons/profile-updated-modal-icon.svg";
import { Toast } from "./shared/Toast";
import { UserService } from "../services";
import UploadPhotoModal from "./modals/uploadPhotoModal";
import Tooltip from "./shared/Tooltip";

export default function Navbar() {
      const getEmailFromToken = () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return '';
        const parts = token.split('.');
        if (parts.length !== 3) return '';
        const base64Url = parts[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        const payload = JSON.parse(jsonPayload);
        return payload.email || payload.username || payload.sub || '';
      } catch (e) {
        return '';
      }
    };
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [isProfileUpdatedModalOpen, setIsProfileUpdatedModalOpen] = useState(false);
  const [passwordChangeSuccessMessage, setPasswordChangeSuccessMessage] = useState(''); 
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeItem, setActiveItem] = useState("dashboard");
  const [isCreateDocumentModalOpen, setIsCreateDocumentModalOpen] = useState(false); 
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false); // new state for logout modal
  
  const navigate = useNavigate();
  const location = useLocation();
    // Get user data from authentication context
  const { user, getUserPicture, updateUserData,isLoading, logout } = useAuthContext();
  
    // Initialize user profile with data from auth context
    const [userProfile, setUserProfile] = useState({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || getEmailFromToken() || "",
      photo: getUserPicture(),
    });
  
    // Update user profile when user data changes (only when user object changes)
    useEffect(() => {
    if (user) {
      setUserProfile({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || getEmailFromToken() || "",
      photo: user.pictureUrl || getUserPicture(),
      });
    }
    }, [user, getUserPicture]);
  
    const handleUploadDrive = () => setIsUploadModalOpen(false);
    const handleUploadDevice = () => setIsUploadModalOpen(false);
    const closeProfileUpdatedOrPasswordChangedModal = () => {
      setIsProfileUpdatedModalOpen(false);
      if (passwordChangeSuccessMessage) {
        logout();
        navigate('/login', { replace: true });
      }
    };
  
    // Updated handler for password change success
    const handleChangePassword = (message) => {
      setIsChangePasswordOpen(false); // Close the change password modal
      setPasswordChangeSuccessMessage(message); // Set the custom success message
      setIsProfileUpdatedModalOpen(true); // Open the profile updated modal
    };
  
    const handleSaveProfile = async (updatedProfile) => {
      try {
        // Prepare profile data for API - only include fields that have values
        const profileData = {};
        
        // Only add fields that have values and are different from current user data
        if (updatedProfile.firstName && updatedProfile.firstName.trim() !== '') {
          profileData.firstName = updatedProfile.firstName.trim();
        }
        if (updatedProfile.lastName && updatedProfile.lastName.trim() !== '') {
          profileData.lastName = updatedProfile.lastName.trim();
        }
        if (updatedProfile.profileImage) {
          profileData.profileImage = updatedProfile.profileImage;
        }
  
        // Check if at least one field is provided
        if (Object.keys(profileData).length === 0) {
          Toast.error('Please provide at least one field to update');
          return;
        }
  
        // Call the update profile API
        const response = await UserService.updateProfile(profileData);
  
        if (response.status === 200 && response.customer) {
          // Update auth context with new customer data
          updateUserData(response.customer);
          
          // Show success toast
          // Toast.success(response.message || 'Profile updated successfully');
  
          // Close modal and show confirmation
          setIsEditProfileModalOpen(false);
          setIsProfileUpdatedModalOpen(true);
        } else {
          // Handle error
          const errorMessage = response.message || 'Failed to update profile';
          Toast.error(errorMessage);
        }
      } catch (error) {
        Toast.error('Network error. Please try again.');
      }
    };

    const handleSidebarItemClick = (item) => {
    // 1. Handle Modal Clicks (Child Items)
    if (item.id === "profile") {
      setIsEditProfileModalOpen(true);
    } else if (item.id === "security") {
      setIsChangePasswordOpen(true);
    } 
    // 2. Handle Logout Modal
    else if (item.id === "logout") {
      setIsLogoutModalOpen(true);
    } 
    // 3. Handle Standard Navigation (Parent Items and others)
    else if (item.path) {
      setActiveItem(item.id);
      navigate(item.path);
    }
  };
  
  // Keep active item in sync with current URL (including on refresh)
  useEffect(() => {
    const allItems = [...mainNavItems, ...otherNavItems];
    const path = location.pathname || '';
    const match = allItems.find((it) => it.path && (path === it.path || path.startsWith(it.path + '/')));
    if (match && match.id !== activeItem) {
      setActiveItem(match.id);
    }
  }, [location.pathname,activeItem]);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  // Get user display name and picture
  const userName = user.firstName ;
  const userPicture = getUserPicture();

  // Show loading state while auth is initializing
  if (isLoading) {
    return (
      <header className="w-full">
        <div className="bg-dark-blue text-white text-[12px] sm:text-[14px] text-center py-1 sm:py-2 px-2 sm:px-4">
          WriteForm is not affiliated with or endorsed by the American Psychological Association.
          References to APA style are for educational and formatting guidance only.
        </div>
        <div className="flex items-center justify-center px-4 sm:px-8 py-3 sm:py-4 bg-white shadow-sm">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dark-blue"></div>
        </div>
      </header>
    );
  }

  return (
    <header className="w-full">
      {/* Top strip */}
      <div className="bg-dark-blue text-white text-[12px] sm:text-[14px] text-center py-1 sm:py-2 px-2 sm:px-4">
        WriteForm is not affiliated with or endorsed by the American Psychological Association.
        References to APA style are for educational and formatting guidance only.
      </div>

      {/* Main nav */}
      <div className="flex items-center justify-between px-4 sm:px-8 py-3 sm:py-4 bg-white shadow-sm">
        <div className="flex items-center cursor-pointer" onClick={() => navigate("/dashboard")}>
          <img src={finalLogo} alt="WriteForm" className="h-7 sm:h-8 w-auto" />
        </div>

        <div className="hidden lg:flex items-center space-x-4 lg:space-x-6">
          {/* <button className="text-dark-blue hover:opacity-80 transition-opacity">
            <img src={bellIcon} alt="Notifications" className="w-5 h-5 lg:w-[22px] lg:h-[22px]" />
          </button> */}

          <button
            className="flex items-center space-x-2 border rounded-md px-2 lg:px-3 py-1 text-xs sm:text-sm font-medium text-dark-blue hover:bg-gray-50 transition-colors"
            onClick={() => {
              if ((location.pathname || '') === '/dashboard') {
                window.dispatchEvent(new Event('dashboard-tour:start'));
              } else {
                // navigate to dashboard before starting as tour is for dashboard page
                navigate('/dashboard');
                setTimeout(() => window.dispatchEvent(new Event('dashboard-tour:start')), 100);
              }
            }}
          >
            {/* <img src={questionIcon} alt="Quick Tour" className="w-4 h-4 lg:w-[22px] lg:h-[22px]" /> */}
            <span>Quick Tour</span>
          </button>

          <div className="h-6 w-px bg-gray"></div>

          <div className="flex items-center text-dark-blue font-medium text-xs sm:text-sm lg:text-base">
            <img src={userPicture} alt="User" className="w-5 h-5 lg:w-[32px] lg:h-[32px] rounded-full mr-2" />
            <span className="hidden lg:flex flex-col leading-tight">
              {/* <span className="font-[400] text-[12px]">Welcome</span> */}
              <span className="font-[500] text-[16px]">{userName}</span>
            </span>
            <span className="flex lg:hidden font-[400] text-[12px]">
               <span className="font-[500] text-[16px] ml-1">{userName}</span>
            </span>
          </div>
        </div>

        <button
          className="lg:hidden text-dark-blue hover:text-[#4b2fbf] transition-colors"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <HamburgerMenuIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="lg:hidden bg-white shadow-sm px-4 py-3 space-y-3">
          {/* <button className="flex items-center space-x-2 text-dark-blue w-full">
            <img src={bellIcon} alt="Notifications" className="w-5 h-5" />
            <span>Notifications</span>
          </button> */}

          <button
            className="flex items-center space-x-2 text-dark-blue w-full"
            onClick={() => {
              if ((location.pathname || '') === '/dashboard') {
                window.dispatchEvent(new Event('dashboard-tour:start'));
              } else {
                navigate('/dashboard');
                setTimeout(() => window.dispatchEvent(new Event('dashboard-tour:start')), 100);
              }
            }}
          >
            {/* <img src={questionIcon} alt="Quick Tour" className="w-5 h-5" /> */}
            <span>Quick Tour</span>
          </button>

          <div className="border-t border-gray-200 my-2"></div>

          <div className="flex items-center space-x-2 text-dark-blue">
            <img src={userPicture} alt="User" className="w-5 h-5 rounded-full" />
            <span className="font-[400]">
               <span className="font-[500]">{userName}</span>
            </span>
          </div>

          {/* Divider for sidebar items */}
          <div className="border-t border-gray-200 my-2"></div>

          <div className="space-y-2">
            <Tooltip content="Start a new paper, thesis, or dissertation with APA style applied.">
              <Button className="w-full" onClick={() => setIsCreateDocumentModalOpen(true)}>
                <div className="flex items-center justify-center gap-2">
                  <Plus className="w-4 h-4 group-hover:text-orange transition-colors duration-200" />
                  <span className="group-hover:text-hover-gradient">New Document</span>
                </div>
              </Button>
            </Tooltip>
            
                <div className="space-y-2">
                  {mainNavItems.map((item) => (
                    <SidebarItem
                      key={item.id}
                      item={item} 
                      activeItem={activeItem}
                      onClick={handleSidebarItemClick}
                    />
                  ))}
                </div>

                <div className="space-y-2.5">
                  {otherNavItems.map((item) => (
                    <SidebarItem
                      key={item.id}
                      item={item}
                      activeItem={activeItem}
                      onClick={handleSidebarItemClick}
                    />
                  ))}
                </div>
          </div>
        </div>
        
      )}
      {/* Create Document Modal */}
      <CreateDocumentModal
        isOpen={isCreateDocumentModalOpen}
        onClose={() => setIsCreateDocumentModalOpen(false)}
        isImported={false}
      />
      <ConfirmationModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        icon={<img src={logoutModalIcon} alt="Logout" className="mx-auto" />}
        heading="Are you logging out?"
        description="You can always log back in at any time and create or edit your documents."
        primaryButtonText="Cancel"
        secondaryButtonText="Logout"
        onPrimaryClick={() => setIsLogoutModalOpen(false)}
        onSecondaryClick={handleLogout}
      />
        {/* Upload Photo Modal */}
        <UploadPhotoModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onUploadDrive={handleUploadDrive}
          onUploadDevice={handleUploadDevice}
        />

        {/* Change Password Modal */}
        <ChangePasswordModal
          isOpen={isChangePasswordOpen}
          onClose={() => setIsChangePasswordOpen(false)}
          onChangePassword={handleChangePassword}
        />

        {/* Edit Profile Modal */}
        <EditProfileModal
          isOpen={isEditProfileModalOpen}
          onClose={() => setIsEditProfileModalOpen(false)}
          userProfile={userProfile}
          onSaveProfile={handleSaveProfile}
        />

        {/* Profile Updated Confirmation Modal */}
        <Modal
          isOpen={isProfileUpdatedModalOpen}
          onClose={() => {
            setIsProfileUpdatedModalOpen(false);
            setPasswordChangeSuccessMessage(''); // Clear message on close
          }}
          icon={<img src={profileUpdatedModalIcon} alt="Profile Updated" className="mx-auto" />}
          heading="Success!"
          description={
            passwordChangeSuccessMessage ||
            ' Your account profile has been updated.'
          }
          buttonText="Close"
          onButtonClick={() => closeProfileUpdatedOrPasswordChangedModal()}
        />
    </header>
  )
}